import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Plugins used app-wide: `customParseFormat` lets `dayjs(str, 'DD.MM.YYYY')`
// parse the editable date-picker fields; `isoWeek` gives the Monday-first week
// the calendar grid + "this week" range presets need; `utc` + `timezone` let
// server stamps render as Tashkent wall time on any viewer zone.
// Registering them here (the single dayjs export) keeps every consumer on
// the same configured instance. Locale is intentionally NOT set globally —
// the app is trilingual, so components localize labels via i18n / `Intl`,
// not a global dayjs locale.
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

/** IANA zone for every customer-facing wall time (HQ: Tashkent, UTC+5). */
export const TASHKENT_ZONE = 'Asia/Tashkent';

/**
 * Server stamps are naive UTC (`YYYY-MM-DDTHH:mm:ss`, no zone designator) —
 * read them as UTC and render Tashkent wall time, independent of the
 * viewer's own zone.
 */
export function tashkentWall(value: string | Date | undefined): Dayjs {
  return dayjs.utc(value).tz(TASHKENT_ZONE);
}

/**
 * Uzbekistan timezone adjustment (UTC+5). When `maxToday` is set and the +5h
 * date falls within a day of now, collapses to the start of today; otherwise
 * returns `date - 5h`. **Business-critical** — every orders list date filter
 * depends on this. Ported verbatim from cpanel `shared/lib/date/toUtcAdjusted`
 * (pinned by `date.test.ts`).
 */
export function toUtcAdjusted(date?: string | Date, maxToday = true): Dayjs {
  const newDate = dayjs(date).add(5, 'hour').format('YYYY-MM-DD');
  if (maxToday && dayjs().diff(dayjs(newDate), 'days') < 1) {
    return dayjs().startOf('day');
  }
  return dayjs(date).subtract(5, 'hour');
}

/** Two-digit zero-padded date/time component. */
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/** `HH:mm` (or `HH:mm:ss`) clock time from an already-validated `Date`. */
export function formatClockTime(date: Date, options?: { seconds?: boolean }): string {
  const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return options?.seconds ? `${clock}:${pad(date.getSeconds())}` : clock;
}

/** `DD.MM.YYYY` calendar day from an already-validated `Date`. */
export function formatDottedDate(date: Date): string {
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * A naive local `YYYY-MM-DDTHH:mm:ss` stamp — the merchant backend's timestamp
 * format (no zone designator; see the "timestamps are naive" wire note). Used
 * for optimistic rows so they sort/render exactly like server-minted ones.
 */
export function naiveNow(): string {
  const at = new Date();
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `T${formatClockTime(at, { seconds: true })}`
  );
}

export { dayjs };
